import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UniversityQuery, University } from '../../types';
import { useUniversitiesSearch } from '../../services/collegeListService';
import { addUniversityToList, removeUniversityFromList } from '../../services/userUniversityService';
import { getRecommendationsForUniversity, registerForOpportunity, RecommendationResponse } from '../../services/demonstratedInterestService';
import { deadlineService } from '../../services/deadlineService';
import FilterSidebar from '../../components/FilterSidebar';
import CollegeCard from '../../components/CollegeCard';
import UniversityModal from '../../components/UniversityModal';
import DemonstratedInterestModal from '../../components/DemonstratedInterestModal';
import './CollegeListBuilder.css';

const CollegeListBuilder: React.FC = () => {
  const queryClient = useQueryClient();
  
  const [filters, setFilters] = useState<UniversityQuery>({
    sort_by: 'name',
    order: 'asc',
    page: 1,
    page_size: 20,
  });

  const [allUniversities, setAllUniversities] = useState<University[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [updatingFavoriteId, setUpdatingFavoriteId] = useState<number | null>(null);
  
  // Modal state
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Demonstrated Interest Modal state
  const [diRecommendation, setDiRecommendation] = useState<RecommendationResponse | null>(null);
  const [isDiModalOpen, setIsDiModalOpen] = useState(false);
  const [pendingUniversityId, setPendingUniversityId] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useUniversitiesSearch(filters);

  // Debounce search to avoid too many API calls
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Update allUniversities when data changes
  useEffect(() => {
    if (data) {
      if (filters.page === 1) {
        // First page or filter change - replace all universities
        setAllUniversities(data.items);
      } else {
        // Load more - preserve scroll position and append to existing universities
        const currentScrollY = window.scrollY;
        setScrollPosition(currentScrollY);
        setAllUniversities(prev => [...prev, ...data.items]);
        
        // Restore scroll position after DOM update
        requestAnimationFrame(() => {
          window.scrollTo(0, currentScrollY);
        });
      }
      setIsLoadingMore(false);
    }
  }, [data, filters.page]);

  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      refetch();
    }, 300);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters.search]);

  const handleFiltersChange = (newFilters: UniversityQuery) => {
    // Reset to page 1 and clear universities when filters change
    setAllUniversities([]);
    setFilters({ ...newFilters, page: 1 });
  };

  const handleFavoriteClick = async (universityId: number) => {
    // Prevent multiple clicks on the same university
    if (updatingFavoriteId === universityId) return;

    // Find the university in our current list
    const university = allUniversities.find(u => u.id === universityId);
    if (!university) return;

    try {
      // Set loading state for this specific university
      setUpdatingFavoriteId(universityId);

      // Optimistic update - immediately update the UI
      setAllUniversities(prev => 
        prev.map(u => 
          u.id === universityId 
            ? { ...u, in_my_list: !u.in_my_list }
            : u
        )
      );

      if (university.in_my_list) {
        // Remove from list
        await removeUniversityFromList(universityId);
        // Refetch data to ensure consistency with backend
        refetch();
      } else {
        // Add to list
        await addUniversityToList(universityId);
        
        // Check for demonstrated interest recommendations
        try {
          const recommendations = await getRecommendationsForUniversity(universityId);
          
          if (recommendations.should_recommend && recommendations.opportunities.length > 0) {
            // Show demonstrated interest modal
            setDiRecommendation(recommendations);
            setPendingUniversityId(universityId);
            setIsDiModalOpen(true);
          } else {
            // No recommendations, just refetch
            refetch();
          }
        } catch (error) {
          console.error('Failed to fetch demonstrated interest recommendations:', error);
          // Continue anyway and refetch
          refetch();
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setAllUniversities(prev => 
        prev.map(u => 
          u.id === universityId 
            ? { ...u, in_my_list: university.in_my_list }
            : u
        )
      );
      
      console.error('Failed to update university list:', error);
      // You could show a toast notification here
    } finally {
      // Clear loading state
      setUpdatingFavoriteId(null);
    }
  };

  const handleLoadMore = () => {
    if (data?.has_next && !isLoadingMore) {
      setIsLoadingMore(true);
      setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  };

  // Modal handlers
  const handleUniversityClick = (university: University) => {
    setSelectedUniversity(university);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUniversity(null);
  };

  // Demonstrated Interest Modal handlers
  const handleAddOpportunities = async (opportunityIds: number[]) => {
    if (!diRecommendation) return;

    try {
      // Register for each selected opportunity and create deadline
      for (const opportunityId of opportunityIds) {
        const opportunity = diRecommendation.opportunities.find(o => o.id === opportunityId);
        if (!opportunity) continue;

        // Register for the opportunity
        await registerForOpportunity({
          opportunity_id: opportunityId,
          completed: false,
        });

        // Create a deadline for this opportunity
        await deadlineService.createDeadline({
          title: opportunity.title,
          info: opportunity.description || '',
          priority: 'medium',
          due_date: opportunity.event_date,
          expected_hours: 1,
          source_type: 'demonstrated_interest',
          university_id: opportunity.university_id,
          completed: false,
        });
      }

      // Close modal and refetch data
      setIsDiModalOpen(false);
      setDiRecommendation(null);
      setPendingUniversityId(null);
      
      // Invalidate deadlines cache to refresh dashboard
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
      
      refetch();
    } catch (error) {
      console.error('Failed to add opportunities:', error);
      // You could show a toast notification here
    }
  };

  const handleDiModalClose = () => {
    setIsDiModalOpen(false);
    setDiRecommendation(null);
    setPendingUniversityId(null);
    // Still refetch to show the university was added
    refetch();
  };

  // Don't block the entire page on error - show error banner instead

  return (
    <div className="college-list-container">
      <div className="main-content">
        {/* Sidebar Filters */}
        <div data-tour="filter-panel">
          <FilterSidebar filters={filters} onFiltersChange={handleFiltersChange} />
        </div>

        {/* Main Results */}
        <div className="results-section">
          {/* Error Banner */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between animate-slideInDown">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Unable to load universities</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Please check your connection and try again</p>
                </div>
              </div>
              <button 
                onClick={() => refetch()}
                className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 rounded-md transition-colors flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry
              </button>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex flex-col space-y-6">
              {/* Loading Header */}
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-48"></div>
              </div>
              
              {/* Loading Cards */}
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white border border-[#e5e7eb] rounded-xl flex p-6 items-center space-x-6 animate-pulse">
                  {/* Image Placeholder */}
                  <div className="rounded-lg w-[120px] h-[90px] bg-gray-200"></div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="space-y-2">
                        <div className="h-6 bg-gray-200 rounded w-64"></div>
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                      </div>
                      <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                    </div>
                    
                    <div className="h-6 bg-gray-200 rounded w-16 mb-4"></div>
                    
                    <div className="flex items-center space-x-12">
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-12"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-8"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-10"></div>
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                      <div className="ml-auto">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="animate-fadeIn">
              {/* Results Header */}
              {data && (
                <div className="text-sm text-gray-600 dark:text-gray-400 transition-opacity duration-300">
                  Showing {allUniversities.length} of {data.total} universities
                  {filters.search && ` for "${filters.search}"`}
                </div>
              )}

              {/* University Cards */}
              <div className="space-y-6">
                {allUniversities.map((university, index) => (
                  <div 
                    key={university.id}
                    className="animate-slideInUp"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CollegeCard
                      university={university}
                      onFavoriteClick={handleFavoriteClick}
                      isUpdatingFavorite={updatingFavoriteId === university.id}
                      onClick={() => handleUniversityClick(university)}
                    />
                  </div>
                ))}
              </div>
              {/* Load More Button */}
              {data && data.has_next && (
                <div className="load-more-container animate-fadeIn">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="load-more-button"
                  >
                    {isLoadingMore ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}

              {/* No Results */}
              {data && allUniversities.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-xl text-gray-600 dark:text-gray-400 mb-2">No universities found</div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">Try adjusting your filters</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* University Details Modal */}
      <UniversityModal
        university={selectedUniversity}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />

      {/* Demonstrated Interest Recommendations Modal */}
      {diRecommendation && (
        <DemonstratedInterestModal
          isOpen={isDiModalOpen}
          onClose={handleDiModalClose}
          recommendation={diRecommendation}
          onAddOpportunities={handleAddOpportunities}
        />
      )}
    </div>
  );
};

export default CollegeListBuilder;
